import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Modal, Form, Alert, Spinner, InputGroup, ButtonGroup } from 'react-bootstrap';
import { FaFolder, FaPlus, FaTrash, FaEdit, FaList, FaTh, FaDoorClosed, FaCheck, FaExclamationTriangle, FaTimes, FaSortAmountDown, FaSortAmountUp, FaCalendarAlt, FaClock, FaCopy } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import projectService from '../services/projectService';
import clientService from '../services/clientService';
import RenderWindow from '../components/RenderWindow';

// Function to determine badge color based on status
const getBadgeColor = (status) => {
  switch (status) {
    case 'Draft':
      return 'bg-info';
    case 'Released for Production':
      return 'bg-success';
    case 'In Production':
      return 'bg-secondary';
    case 'Completed':
      return 'bg-success';
    case 'Delivered':
      return 'bg-dark';
    default:
      return 'bg-light text-dark';
  }
};

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detailed'
  const [sortBy, setSortBy] = useState('created_at'); // 'created_at', 'due_date', 'project_number', 'name'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [dueDateFilter, setDueDateFilter] = useState('all'); // 'all', 'overdue', 'due_soon', 'no_date'

  // State for project creation modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [projectNumber, setProjectNumber] = useState('');
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectDueDate, setProjectDueDate] = useState('');
  const [projectStatus, setProjectStatus] = useState('Draft');
  const [projectClientId, setProjectClientId] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  // State for copy project modal
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [projectToCopy, setProjectToCopy] = useState(null);
  const [copyProjectNumber, setCopyProjectNumber] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyError, setCopyError] = useState('');

  // Helper function to count total files in a project
  const getTotalFileCount = (project) => {
    const projectFiles = project.files?.length || 0;
    const folderFiles = project.folders?.reduce((sum, folder) => sum + (folder.files?.length || 0), 0) || 0;
    return projectFiles + folderFiles;
  };

  // Helper function to check if a door folder has PDF drawings
  const hasPdfDrawings = (folder) => {
    if (!folder || !folder.files || folder.files.length === 0) {
      return false;
    }
    return folder.files.some(file => 
      file.file_type === 'application/pdf' || 
      file.filename?.toLowerCase().endsWith('.pdf')
    );
  };

  // Helper function to get door drawing status for a project
  const getDoorDrawingStatus = (project) => {
    const doorFolders = project.folders?.filter(f => f.folder_type === 'door') || [];
    
    if (doorFolders.length === 0) {
      return { status: 'no-doors', doorsWithDrawings: 0, totalDoors: 0 };
    }

    const doorsWithDrawings = doorFolders.filter(hasPdfDrawings).length;
    const totalDoors = doorFolders.length;

    if (doorsWithDrawings === 0) {
      return { status: 'no-drawings', doorsWithDrawings, totalDoors };
    } else if (doorsWithDrawings === totalDoors) {
      return { status: 'all-complete', doorsWithDrawings, totalDoors };
    } else {
      return { status: 'partial', doorsWithDrawings, totalDoors };
    }
  };

  // Helper function to get due date from project (handles both camelCase and snake_case)
  const getProjectDueDate = (project) => {
    return project.dueDate || project.due_date || null;
  };

  // Helper function to check if a project is overdue
  const isOverdue = (project) => {
    const dueDate = getProjectDueDate(project);
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  // Helper function to check if a project is due soon (within 7 days)
  const isDueSoon = (project) => {
    const dueDate = getProjectDueDate(project);
    if (!dueDate) return false;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  // Helper function to get due date status
  const getDueDateStatus = (project) => {
    const dueDate = getProjectDueDate(project);
    if (!dueDate) return 'no-date';
    if (isOverdue(project)) return 'overdue';
    if (isDueSoon(project)) return 'due-soon';
    return 'normal';
  };

  // Fetch all projects
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService.getProjects();
      
      // Map door metadata from snake_case to camelCase for UI compatibility
      const mappedProjects = data.map(project => ({
        ...project,
        projectNumber: project.project_number,
        folders: project.folders?.map(folder => ({
          ...folder,
          doorMetadata: folder.door_metadata && folder.door_metadata.length > 0 ? {
            id: folder.door_metadata[0].id,
            width: folder.door_metadata[0].width,
            height: folder.door_metadata[0].height,
            doorName: folder.door_metadata[0].door_name,
            hasGlass: folder.door_metadata[0].has_glass,
            hingeType: folder.door_metadata[0].hinge_type,
            lockType: folder.door_metadata[0].lock_type,
            amount: folder.door_metadata[0].amount,
            waternose: folder.door_metadata[0].waternose,
            thresholdType: folder.door_metadata[0].threshold_type,
            kickplate: folder.door_metadata[0].kickplate,
            surfaceFinish: folder.door_metadata[0].surface_finish,
            customFinish: folder.door_metadata[0].custom_finish,
            colorCode: folder.door_metadata[0].color_code,
            handedness: folder.door_metadata[0].handedness
          } : null
        }))
      }));
      
      // Calculate file statistics using helper function
      const totalFiles = mappedProjects.reduce((sum, p) => sum + getTotalFileCount(p), 0);

      // Calculate drawing statistics
      const drawingStats = mappedProjects.reduce((stats, p) => {
        const drawingStatus = getDoorDrawingStatus(p);
        switch (drawingStatus.status) {
          case 'all-complete':
            stats.projectsAllComplete++;
            break;
          case 'partial':
            stats.projectsPartial++;
            break;
          case 'no-drawings':
            stats.projectsNoDrawings++;
            break;
          case 'no-doors':
            stats.projectsNoDoors++;
            break;
        }
        stats.totalDoorsWithDrawings += drawingStatus.doorsWithDrawings;
        stats.totalDoors += drawingStatus.totalDoors;
        return stats;
      }, {
        projectsAllComplete: 0,
        projectsPartial: 0,
        projectsNoDrawings: 0,
        projectsNoDoors: 0,
        totalDoorsWithDrawings: 0,
        totalDoors: 0
      });

      console.log('📊 Dashboard: Projects loaded:', {
        totalProjects: mappedProjects.length,
        projectsWithDoors: mappedProjects.filter(p => p.folders?.some(f => f.folder_type === 'door')).length,
        totalDoors: mappedProjects.reduce((sum, p) => sum + (p.folders?.filter(f => f.folder_type === 'door').length || 0), 0),
        totalFiles: totalFiles,
        drawingStats: drawingStats,
        projectBreakdown: mappedProjects.map(p => {
          const drawingStatus = getDoorDrawingStatus(p);
          return {
            project: p.project_number,
            projectFiles: p.files?.length || 0,
            folderFiles: p.folders?.reduce((folderSum, f) => folderSum + (f.files?.length || 0), 0) || 0,
            totalFiles: getTotalFileCount(p),
            drawingStatus: drawingStatus.status,
            doorsWithDrawings: `${drawingStatus.doorsWithDrawings}/${drawingStatus.totalDoors}`,
            dueDate: getProjectDueDate(p),
            dueDateRaw: { dueDate: p.dueDate, due_date: p.due_date }
          };
        })
      });
      
      setProjects(mappedProjects);
      setError('');
    } catch (err) {
      setError('Failed to fetch projects. Please try again.');
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all clients
  const fetchClients = async () => {
    try {
      const data = await clientService.getClients(true); // Only active clients
      setClients(data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchClients();
  }, []);

  // Handle creating new project
  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    // Validate project number is numeric
    const numberRegex = /^\d+$/;
    if (!numberRegex.test(projectNumber)) {
      setCreateError('Please enter only numbers for the project number');
      return;
    }
    
    // Format with CO- prefix
    const formattedNumber = `CO-${projectNumber}`;

    try {
      setCreateLoading(true);
      setCreateError('');
      
      // Check if project number already exists
      const exists = await projectService.checkProjectNumber(formattedNumber.toUpperCase());
      if (exists) {
        setCreateError('Project number already exists');
        return;
      }
      
      await projectService.createProject({
        projectNumber: formattedNumber.toUpperCase(),
        name: projectName,
        description: projectDescription,
        dueDate: projectDueDate,
        status: projectStatus,
        clientId: projectClientId || null
      });
      
      toast.success('Project created successfully');
      setShowCreateModal(false);
      
      // Reset form
      setProjectNumber('');
      setProjectName('');
      setProjectDescription('');
      setProjectDueDate('');
      setProjectStatus('Draft');
      setProjectClientId('');
      
      // Refresh projects list
      fetchProjects();
    } catch (err) {
      setCreateError(err.message || 'Failed to create project');
      console.error('Error creating project:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  // Handle deleting a project
  const handleDeleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project? All files will be permanently deleted.')) {
      try {
        await projectService.deleteProject(id);
        toast.success('Project deleted successfully');
        
        // Refresh projects list
        fetchProjects();
      } catch (err) {
        toast.error(err.message || 'Failed to delete project');
        console.error('Error deleting project:', err);
      }
    }
  };

  // Handle opening copy project modal
  const handleOpenCopyModal = (project) => {
    setProjectToCopy(project);
    setCopyProjectNumber('');
    setCopyError('');
    setShowCopyModal(true);
  };

  // Handle copying a project
  const handleCopyProject = async (e) => {
    e.preventDefault();
    
    // Validate project number is numeric
    const numberRegex = /^\d+$/;
    if (!numberRegex.test(copyProjectNumber)) {
      setCopyError('Please enter only numbers for the project number');
      return;
    }
    
    // Format with CO- prefix
    const formattedNumber = `CO-${copyProjectNumber}`;

    try {
      setCopyLoading(true);
      setCopyError('');
      
      // Check if project number already exists
      const exists = await projectService.checkProjectNumber(formattedNumber.toUpperCase());
      if (exists) {
        setCopyError('Project number already exists');
        return;
      }
      
      // Copy project with all folders, door metadata, and file links
      await projectService.copyProject(
        projectToCopy.id,
        formattedNumber.toUpperCase()
      );
      
      toast.success('Project copied successfully with all doors and files');
      setShowCopyModal(false);
      setCopyProjectNumber('');
      setProjectToCopy(null);
      
      // Refresh projects list
      fetchProjects();
    } catch (err) {
      setCopyError(err.message || 'Failed to copy project');
      console.error('Error copying project:', err);
    } finally {
      setCopyLoading(false);
    }
  };

  // Filter and sort projects
  const filteredAndSortedProjects = (() => {
    let filtered = projects;

    // Filter by client
    if (selectedClient) {
      filtered = filtered.filter(project => project.client_id === selectedClient);
    }

    // Filter by due date
    if (dueDateFilter !== 'all') {
      filtered = filtered.filter(project => {
        const status = getDueDateStatus(project);
        switch (dueDateFilter) {
          case 'overdue':
            return status === 'overdue';
          case 'due_soon':
            return status === 'due-soon';
          case 'no_date':
            return status === 'no-date';
          default:
            return true;
        }
      });
    }

    // Sort projects
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'due_date':
          aValue = getProjectDueDate(a) ? new Date(getProjectDueDate(a)) : new Date('9999-12-31');
          bValue = getProjectDueDate(b) ? new Date(getProjectDueDate(b)) : new Date('9999-12-31');
          break;
        case 'project_number':
          aValue = a.project_number || '';
          bValue = b.project_number || '';
          break;
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  })();

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Projects</h2>
        <Button 
          variant="primary"
          onClick={() => setShowCreateModal(true)}
        >
          <FaPlus className="me-1" /> New Project
        </Button>
      </div>

      {/* Filters and Controls */}
      <Row className="mb-4">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Filter by Client</Form.Label>
            <Form.Select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
            >
              <option value="">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name} {client.company_name && `(${client.company_name})`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Due Date Filter</Form.Label>
            <Form.Select
              value={dueDateFilter}
              onChange={(e) => setDueDateFilter(e.target.value)}
            >
              <option value="all">All Projects</option>
              <option value="overdue">Overdue</option>
              <option value="due_soon">Due Soon (7 days)</option>
              <option value="no_date">No Due Date</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Sort By</Form.Label>
            <InputGroup>
              <Form.Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="created_at">Created Date</option>
                <option value="due_date">Due Date</option>
                <option value="project_number">Project Number</option>
                <option value="name">Project Name</option>
              </Form.Select>
              <Button
                variant="outline-secondary"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                {sortOrder === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
              </Button>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <Form.Group>
            <Form.Label>View Mode</Form.Label>
            <div>
              <ButtonGroup>
                <Button
                  variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('list')}
                >
                  <FaList className="me-1" /> List
                </Button>
                <Button
                  variant={viewMode === 'detailed' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('detailed')}
                >
                  <FaTh className="me-1" /> Detailed
                </Button>
              </ButtonGroup>
            </div>
          </Form.Group>
        </Col>
      </Row>

      {/* Results Summary */}
      {!loading && projects.length > 0 && (
        <Row className="mb-3">
          <Col>
            <Alert variant="light" className="mb-0">
              <small>
                Showing <strong>{filteredAndSortedProjects.length}</strong> of <strong>{projects.length}</strong> projects
                {selectedClient && (
                  <span> • Filtered by client</span>
                )}
                {dueDateFilter !== 'all' && (
                  <span> • {dueDateFilter === 'overdue' ? 'Overdue projects' : 
                            dueDateFilter === 'due_soon' ? 'Due soon (7 days)' : 
                            'Projects with no due date'}</span>
                )}
                {sortBy !== 'created_at' && (
                  <span> • Sorted by {sortBy === 'due_date' ? 'due date' : 
                                      sortBy === 'project_number' ? 'project number' : 
                                      'project name'} ({sortOrder === 'asc' ? 'ascending' : 'descending'})</span>
                )}
              </small>
            </Alert>
          </Col>
        </Row>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : projects.length === 0 ? (
        <Alert variant="info">
          No projects found. Create your first project by clicking the "New Project" button.
        </Alert>
      ) : filteredAndSortedProjects.length === 0 ? (
        <Alert variant="info">
          No projects found for the selected filters.
        </Alert>
      ) : viewMode === 'list' ? (
        <Row xs={1} md={2} lg={3} className="g-4">
          {filteredAndSortedProjects.map(project => (
            <Col key={project.id}>
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                      <div className="position-relative me-3">
                        <FaFolder className="text-primary" size={32} />
                      </div>
                      <div>
                        <Card.Title className="mb-0">{project.projectNumber}</Card.Title>
                        <small className="text-muted">{project.name}</small>
                      </div>
                    </div>
                    <div>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleOpenCopyModal(project)}
                        title="Copy Project"
                      >
                        <FaCopy />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeleteProject(project.id)}
                        title="Delete Project"
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Client Info */}
                  {project.client && (
                    <div className="mb-2">
                      <small className="text-muted">
                        <strong>Client:</strong> {project.client.name}
                        {project.client.company_name && ` (${project.client.company_name})`}
                      </small>
                    </div>
                  )}
                  
                  <Card.Text>
                    {project.description?.substring(0, 80)}
                    {project.description?.length > 80 ? '...' : ''}
                  </Card.Text>
                  
                  {/* Status Badge */}
                  <div className="mb-2">
                    <span className={`badge ${getBadgeColor(project.status)}`}>
                      {project.status || 'New'}
                    </span>
                    
                    {getProjectDueDate(project) ? (
                      (() => {
                        const dueDateStatus = getDueDateStatus(project);
                        const formattedDate = new Date(getProjectDueDate(project)).toLocaleDateString();
                        
                        switch (dueDateStatus) {
                          case 'overdue':
                            return (
                              <span className="ms-2 text-danger">
                                <FaClock className="me-1" size={12} />
                                Overdue: {formattedDate}
                              </span>
                            );
                          case 'due-soon':
                            return (
                              <span className="ms-2 text-warning">
                                <FaCalendarAlt className="me-1" size={12} />
                                Due Soon: {formattedDate}
                              </span>
                            );
                          default:
                            return (
                              <span className="ms-2 text-muted">
                                <FaCalendarAlt className="me-1" size={12} />
                                Due: {formattedDate}
                              </span>
                            );
                        }
                      })()
                    ) : (
                      <span className="ms-2 text-muted">
                        <FaCalendarAlt className="me-1" size={12} />
                        No due date
                      </span>
                    )}
                  </div>
                  
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex flex-column">
                      <small className="text-muted">
                        Files: {getTotalFileCount(project)}
                      </small>
                      {(() => {
                        const drawingStatus = getDoorDrawingStatus(project);
                        switch (drawingStatus.status) {
                          case 'no-doors':
                            return (
                              <small className="text-muted">
                                <FaDoorClosed className="me-1" size={12} />
                                No doors
                              </small>
                            );
                          case 'all-complete':
                            return (
                              <small className="text-success">
                                <FaCheck className="me-1" size={12} />
                                All doors have drawings ({drawingStatus.totalDoors})
                              </small>
                            );
                          case 'partial':
                            return (
                              <small className="text-warning">
                                <FaExclamationTriangle className="me-1" size={12} />
                                {drawingStatus.doorsWithDrawings}/{drawingStatus.totalDoors} doors have drawings
                              </small>
                            );
                          case 'no-drawings':
                            return (
                              <small className="text-danger">
                                <FaTimes className="me-1" size={12} />
                                No drawings ({drawingStatus.totalDoors} doors)
                              </small>
                            );
                          default:
                            return null;
                        }
                      })()}
                    </div>
                    <Link 
                      to={`/admin/project/${project.id}`}
                      className="btn btn-sm btn-outline-primary"
                    >
                      View Details
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        // Detailed View with Door Previews
        <Row xs={1} md={1} lg={2} className="g-4">
          {filteredAndSortedProjects.map(project => (
            <Col key={project.id}>
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                      <div className="position-relative me-3">
                        <FaFolder className="text-primary" size={40} />
                      </div>
                      <div>
                        <Card.Title className="mb-1">{project.projectNumber}</Card.Title>
                        <Card.Subtitle className="text-muted mb-2">
                          {project.name}
                        </Card.Subtitle>
                        <Card.Subtitle className="text-muted">
                          {project.client && (
                            <span>
                              {project.client.name}
                              {project.client.company_name && ` (${project.client.company_name})`}
                            </span>
                          )}
                        </Card.Subtitle>
                      </div>
                    </div>
                    <div>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleOpenCopyModal(project)}
                        title="Copy Project"
                      >
                        <FaCopy />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeleteProject(project.id)}
                        title="Delete Project"
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </div>

                  {/* Project Description */}
                  {project.description && (
                    <Card.Text className="mb-3">
                      {project.description}
                    </Card.Text>
                  )}

                  {/* Status and Due Date */}
                  <div className="mb-3 d-flex justify-content-between align-items-center">
                    <span className={`badge ${getBadgeColor(project.status)}`}>
                      {project.status || 'New'}
                    </span>
                    {getProjectDueDate(project) ? (
                      (() => {
                        const dueDateStatus = getDueDateStatus(project);
                        const formattedDate = new Date(getProjectDueDate(project)).toLocaleDateString();
                        
                        switch (dueDateStatus) {
                          case 'overdue':
                            return (
                              <small className="text-danger">
                                <FaClock className="me-1" size={12} />
                                Overdue: {formattedDate}
                              </small>
                            );
                          case 'due-soon':
                            return (
                              <small className="text-warning">
                                <FaCalendarAlt className="me-1" size={12} />
                                Due Soon: {formattedDate}
                              </small>
                            );
                          default:
                            return (
                              <small className="text-muted">
                                <FaCalendarAlt className="me-1" size={12} />
                                Due: {formattedDate}
                              </small>
                            );
                        }
                      })()
                    ) : (
                      <small className="text-muted">
                        <FaCalendarAlt className="me-1" size={12} />
                        No due date
                      </small>
                    )}
                  </div>

                  {/* Door Previews */}
                  {project.folders && project.folders.length > 0 ? (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                          <FaDoorClosed className="me-1" /> 
                          Doors ({project.folders.filter(f => f.folder_type === 'door').length})
                        </h6>
                        {(() => {
                          const drawingStatus = getDoorDrawingStatus(project);
                          switch (drawingStatus.status) {
                            case 'all-complete':
                              return (
                                <small className="text-success">
                                  <FaCheck className="me-1" size={12} />
                                  All have drawings
                                </small>
                              );
                            case 'partial':
                              return (
                                <small className="text-warning">
                                  <FaExclamationTriangle className="me-1" size={12} />
                                  {drawingStatus.doorsWithDrawings}/{drawingStatus.totalDoors} with drawings
                                </small>
                              );
                            case 'no-drawings':
                              return (
                                <small className="text-danger">
                                  <FaTimes className="me-1" size={12} />
                                  No drawings
                                </small>
                              );
                            default:
                              return null;
                          }
                        })()}
                      </div>
                      <Row xs={2} md={3} lg={4} className="g-2 mb-3">
                        {project.folders
                          .filter(folder => folder.folder_type === 'door')
                          .slice(0, 8) // Show max 8 doors
                          .map(folder => (
                            <Col key={folder.id}>
                              <div className="text-center p-2 border rounded position-relative">
                                {folder.doorMetadata ? (
                                  <RenderWindow 
                                    doorMetadata={folder.doorMetadata}
                                    size="small"
                                    showDetails={false}
                                  />
                                ) : (
                                  <FaDoorClosed className="text-muted" size={32} />
                                )}
                                
                                {/* Drawing status indicator */}
                                {hasPdfDrawings(folder) && (
                                  <span className="position-absolute top-0 end-0 translate-middle badge rounded-pill bg-success" style={{ fontSize: '0.5rem' }}>
                                    <FaCheck size={8} />
                                  </span>
                                )}
                                
                                <div className="mt-1">
                                  <small className="text-muted">{folder.name}</small>
                                </div>
                              </div>
                            </Col>
                          ))}
                      </Row>
                      {project.folders.filter(f => f.folder_type === 'door').length > 8 && (
                        <small className="text-muted">
                          +{project.folders.filter(f => f.folder_type === 'door').length - 8} more doors
                        </small>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <FaDoorClosed className="text-muted mb-2" size={32} />
                      <div className="text-muted">No doors created yet</div>
                    </div>
                  )}

                  {/* View Project Button */}
                  <div className="mt-3">
                    <Link to={`/admin/project/${project.id}`} className="btn btn-primary w-100">
                      View Project Details
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Create Project Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createError && <Alert variant="danger">{createError}</Alert>}
          
          <Form onSubmit={handleCreateProject}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Number</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>CO-</InputGroup.Text>
                    <Form.Control 
                      type="text" 
                      value={projectNumber}
                      onChange={(e) => setProjectNumber(e.target.value)}
                      placeholder="12345"
                      required
                    />
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Enter only numbers. The "CO-" prefix will be added automatically.
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Control
                    as="select"
                    value={projectStatus}
                    onChange={(e) => setProjectStatus(e.target.value)}
                  >
                    <option value="Draft">Draft</option>
                    <option value="Released for Production">Released for Production</option>
                    <option value="In Production">In Production</option>
                    <option value="Completed">Completed</option>
                    <option value="Delivered">Delivered</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Client</Form.Label>
              <Form.Select
                value={projectClientId}
                onChange={(e) => setProjectClientId(e.target.value)}
              >
                <option value="">Select a client (optional)</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name} {client.company_name && `(${client.company_name})`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control 
                type="text" 
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Enter project name"
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3}
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                placeholder="Enter project description"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Due Date</Form.Label>
              <Form.Control
                type="date"
                value={projectDueDate}
                onChange={(e) => setProjectDueDate(e.target.value)}
              />
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button 
                variant="secondary" 
                onClick={() => setShowCreateModal(false)} 
                className="me-2"
                disabled={createLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={createLoading}
              >
                {createLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Creating...
                  </>
                ) : (
                  'Create Project'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Copy Project Modal */}
      <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaCopy className="me-2" />
            Copy Project
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {copyError && <Alert variant="danger">{copyError}</Alert>}
          
          {projectToCopy && (
            <Alert variant="info">
              <strong>Copying:</strong> {projectToCopy.projectNumber} - {projectToCopy.name}
              <br />
              <small>
                The new project will include all doors, specifications, and file links.
                <br />
                Status will be set to "Draft". Files are linked, not duplicated.
              </small>
            </Alert>
          )}
          
          <Form onSubmit={handleCopyProject}>
            <Form.Group className="mb-3">
              <Form.Label>New Project Number</Form.Label>
              <InputGroup>
                <InputGroup.Text>CO-</InputGroup.Text>
                <Form.Control 
                  type="text" 
                  value={copyProjectNumber}
                  onChange={(e) => setCopyProjectNumber(e.target.value)}
                  placeholder="12345"
                  required
                  autoFocus
                />
              </InputGroup>
              <Form.Text className="text-muted">
                Enter only numbers. The "CO-" prefix will be added automatically.
              </Form.Text>
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button 
                variant="secondary" 
                onClick={() => setShowCopyModal(false)} 
                className="me-2"
                disabled={copyLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={copyLoading}
              >
                {copyLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Copying...
                  </>
                ) : (
                  'Copy Project'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Dashboard;
